/**
 * LLM Model types — mirrors backend ModelResponse + OrgDefaultResponse DTOs.
 * Capability field names match aic.CapabilitiesWire struct fields (camelCase wire convention).
 */

export type Capabilities = {
  chat: boolean;
  embed: boolean;
  vision: boolean;
  transcription: boolean;
  toolUse: boolean;
  reasoning: boolean;
  imageGen: boolean;
  videoGen: boolean;
  tts: boolean;
};

// Polymorphic pricing — discriminated by `kind`. Mirrors the BE
// PricingPreset shape (share/component/aic/pricing.go). Each kind has
// a distinct cost model: per-token for chat/embed, per-image tier list
// for imageGen, per-second for videoGen, per-million-chars for TTS,
// per-minute for STT, monthly fee for subscription. Fields not relevant
// to the kind arrive as `undefined` from BE (json:omitempty).
export type ModelPricing = {
  kind: "token" | "image" | "video" | "tts" | "stt" | "subscription";
  currency?: string;
  // token kind
  input_per_1m?: number;
  output_per_1m?: number;
  cached_input_per_1m?: number;
  cache_write_per_1m?: number;
  reasoning_per_1m?: number;
  // video / tts / stt
  per_second?: number;
  per_million_chars?: number;
  per_minute?: number;
  // subscription
  monthly_fee?: number;
  // image — list of size-tier price points
  image_tiers?: Array<{ tier_key: string; per_image: number }>;
};

/** Origin of the model row — drives the source badge + sync UX in ModelTableRow. */
export type ModelSource = "dynamic" | "catalog" | "custom";

export type LLMModel = {
  id: string;
  /** null for system catalog models (isSystem=true). */
  providerId: string | null;
  isSystem: boolean;
  modelId: string;
  displayName: string;
  capabilities: Capabilities;
  contextWindow?: number | null;
  maxOutputTokens?: number | null;
  embedDimension?: number | null;
  pricing?: ModelPricing | null;
  enabled: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  /** Baseline schema surface — present on every row from BE; unknown values fall back to "custom" at the badge layer. */
  source: ModelSource;
  lastSyncedAt?: string | null;
  probeVerifiedAt?: string | null;
  /** Per-field mask: when admin edits a field, the key is set true so subsequent provider sync skips overwriting it. */
  adminOverrides: Record<string, boolean>;
  /** Backend-owned per-use-case selection policy. Missing entries mean eligible. */
  useCaseEligibility?: Record<string, ModelUseCaseEligibility>;
};

export type ModelUseCaseEligibility = {
  eligible: boolean;
  reason?: string;
};

/** Probe response wire shape — mirrors aicdto.ProbeModelResult (backend-go/modules/aiconfig/dto/probe_dto.go). */
export type ProbeDetected = {
  embedDimension?: number;
  maxOutputTokens?: number;
};

export type ProbeModelResult = {
  /** Per-cap pass/fail keyed by cap slug ("chat" | "embed"). */
  verifiedCapabilities: Record<string, boolean>;
  detected: ProbeDetected;
  /** Per-cap failure detail; empty when every requested cap passed. */
  errors?: Record<string, string>;
  probedAt: string;
};

export type ProbeModelInput = {
  providerId: string;
  modelId: string;
  capabilitiesToTest: string[];
};

export type OrgDefault = {
  useCase: string;
  provider: {
    id: string;
    name: string;
    displayName: string;
    providerType: string;
  };
  model: LLMModel;
};

/** Use-case values matching BE CHECK constraint (L_use_case_check).
 *  `extract_kg` is the dedicated KG extraction binding — eligibility is
 *  chat-capable models, but the binding lives separately so admins can
 *  pick a cheaper/faster model for the extraction pipeline without
 *  touching the chat workspace default. */
export const USE_CASES = [
  "embedding",
  "chat",
  "vision",
  "transcription",
  "transcription_refine",
  "extract_kg",
] as const;
export type UseCase = typeof USE_CASES[number];

/** Maps useCase to the Capabilities field that must be true. */
export const USE_CASE_CAPABILITY_MAP: Record<UseCase, keyof Capabilities> = {
  embedding: "embed",
  chat: "chat",
  vision: "vision",
  transcription: "transcription",
  transcription_refine: "chat",
  // KG extraction needs a chat-capable model — same predicate as 'chat'.
  extract_kg: "chat",
};

export type CreateModelInput = {
  modelId: string;
  displayName?: string;
  capabilities: Capabilities;
  contextWindow?: number;
  maxOutputTokens?: number;
  embedDimension?: number;
  pricing?: import("./llm-pricing").PricingPreset;
  enabled?: boolean;
  metadata?: Record<string, unknown>;
};

export type UpdateModelInput = {
  displayName?: string;
  capabilities?: Capabilities;
  contextWindow?: number;
  maxOutputTokens?: number;
  embedDimension?: number;
  pricing?: import("./llm-pricing").PricingPreset;
  enabled?: boolean;
  metadata?: Record<string, unknown>;
};
